import type { CSSProperties, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { Card, TextInput } from './ui';

import { useT } from '../i18n/messages';

export interface TableColumn<T = Record<string, unknown>> {
  key: string;
  label: string;
  className?: string;
  maxWidth?: number;
  render?: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number | null | undefined;
  searchValue?: (row: T) => string;
  truncate?: boolean;
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (Array.isArray(value)) {
    return value.map((entry) => stringifyValue(entry)).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function TableCellContent({
  content,
  maxWidth,
  truncate,
}: {
  content: ReactNode;
  maxWidth?: number;
  truncate: boolean;
}) {
  if (typeof content === 'string' || typeof content === 'number') {
    const text = String(content);
    return (
      <span
        className={truncate ? 'table-cell-text table-cell-text--truncate' : 'table-cell-text'}
        style={
          maxWidth ? ({ '--table-cell-max-width': `${maxWidth}px` } as CSSProperties) : undefined
        }
        title={text}
      >
        {text}
      </span>
    );
  }

  return (
    <div
      className={truncate ? 'table-cell-custom table-cell-custom--truncate' : 'table-cell-custom'}
      style={
        maxWidth ? ({ '--table-cell-max-width': `${maxWidth}px` } as CSSProperties) : undefined
      }
    >
      {content}
    </div>
  );
}

export function DataTable<T>({
  columns,
  emptyMessage,
  pageSize,
  rows,
  searchable = false,
  searchPlaceholder,
  title,
}: {
  columns: TableColumn<T>[];
  emptyMessage?: string;
  pageSize?: number;
  rows: T[];
  searchable?: boolean;
  searchPlaceholder?: string;
  title?: ReactNode;
}) {
  const t = useT();
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('billing.ui.search.records');
  const [query, setQuery] = useState('');
  const [sortState, setSortState] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const nextRows = normalizedQuery
      ? rows.filter((row) =>
          columns.some((column) => {
            const columnValue = column.searchValue
              ? column.searchValue(row)
              : stringifyValue((row as Record<string, unknown>)[column.key]);
            return columnValue.toLowerCase().includes(normalizedQuery);
          }),
        )
      : rows;

    if (!sortState) {
      return nextRows;
    }

    const column = columns.find((entry) => entry.key === sortState.key);
    if (!column) {
      return nextRows;
    }

    return [...nextRows].sort((left, right) => {
      const leftValue =
        column.sortValue?.(left) ?? stringifyValue((left as Record<string, unknown>)[column.key]);
      const rightValue =
        column.sortValue?.(right) ?? stringifyValue((right as Record<string, unknown>)[column.key]);

      if (leftValue === rightValue) {
        return 0;
      }

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return sortState.direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
      }

      return sortState.direction === 'asc'
        ? String(leftValue).localeCompare(String(rightValue))
        : String(rightValue).localeCompare(String(leftValue));
    });
  }, [columns, query, rows, sortState]);

  const totalPages = pageSize ? Math.max(1, Math.ceil(filteredRows.length / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const pagedRows = pageSize
    ? filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : filteredRows;

  const hasData = pagedRows.length > 0;

  return (
    <Card className="table-card panel page-panel">
      {title || searchable ? (
        <div className="table-toolbar">
          {title ? <div className="table-toolbar__title">{title}</div> : <span />}
          {searchable ? (
            <TextInput
              aria-label={resolvedSearchPlaceholder}
              className="table-toolbar__search"
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder={resolvedSearchPlaceholder}
              value={query}
            />
          ) : null}
        </div>
      ) : null}
      <div className="table-scroll" role="region" aria-label={`${title ?? 'Records'} table, scroll horizontally for more columns`} tabIndex={0}>
        <table>
          <thead>
            <tr>
              {columns.map((column) => {
                const activeSort = sortState?.key === column.key ? sortState.direction : null;
                return (
                  <th key={column.key} className={column.className}>
                    <button
                      className={activeSort ? 'table-sort table-sort--active' : 'table-sort'}
                      onClick={() =>
                        setSortState((current) =>
                          current?.key === column.key
                            ? {
                                key: column.key,
                                direction: current.direction === 'asc' ? 'desc' : 'asc',
                              }
                            : { key: column.key, direction: 'asc' },
                        )
                      }
                      type="button"
                    >
                      <span>{t(column.label)}</span>
                      <span aria-hidden="true">{activeSort === 'desc' ? '↓' : '↑'}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {hasData ? (
              pagedRows.map((row, rowIndex) => (
                <tr key={String((row as { id?: string | number }).id ?? rowIndex)}>
                  {columns.map((column) => {
                    const content = column.render
                      ? column.render(row)
                      : stringifyValue((row as Record<string, unknown>)[column.key]);
                    return (
                      <td key={column.key} className={column.className}>
                        <TableCellContent
                          content={content}
                          maxWidth={column.maxWidth}
                          truncate={column.truncate ?? column.className !== 'table-actions-cell'}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td className="table-empty" colSpan={columns.length}>
                  {emptyMessage ?? t('billing.resource.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pageSize && filteredRows.length > pageSize ? (
        <footer className="pagination table-pagination">
          <button
            className="ghost-button"
            disabled={currentPage <= 1}
            onClick={() => setPage((entry) => Math.max(1, entry - 1))}
            type="button"
          >
            {t('billing.resource.previous')}
          </button>
          <span className="pagination-summary">
            {t('billing.pagination.summary', {
              page: currentPage,
              total: totalPages,
            })}
          </span>
          <button
            className="ghost-button"
            disabled={currentPage >= totalPages}
            onClick={() => setPage((entry) => Math.min(totalPages, entry + 1))}
            type="button"
          >
            {t('billing.resource.next')}
          </button>
        </footer>
      ) : null}
    </Card>
  );
}
